import { HttpContext } from "@angular/common/http";
import { Component, OnInit, inject } from "@angular/core";
import { ConfirmationService, MessageService } from "primeng/api";
import { Observable } from "rxjs";
import {
  ChebiEntry,
  ChebiEntryControllerService,
  EntityModelChebiEntry,
  EntityModelLipidMapsEntry,
  LipidMapsEntry,
  LipidMapsEntryControllerService,
  PagedModelEntityModelChebiEntry,
  PagedModelEntityModelLipidMapsEntry
} from "../../../../modules/lipidcompass-backend-client";
import { CrudAdapter } from "../../layouts/crud-table/crud-adapter";
import { ColumnFilterType, TableColumn } from "../../_models/table-column";
import {
  JobsService,
  JobFileInputDto
} from "../../../../modules/lipidcompass-data-importer-client";
import { LoggingService } from "../../_services/logging.service";
import { CrudTableComponent } from "../../layouts/crud-table/crud-table.component";
import { SelectModule } from "primeng/select";
import { ButtonModule } from "primeng/button";
import { ButtonGroupModule } from "primeng/buttongroup";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { FormsModule } from "@angular/forms";
import { ToastModule } from "primeng/toast";

@Component({
  selector: "app-admin-chebi",
  templateUrl: "./admin-chebi.component.html",
  styleUrls: ["./admin-chebi.component.css"],
  standalone: true,
  imports: [
    ButtonModule,
    ButtonGroupModule,
    ConfirmDialogModule,
    CrudTableComponent,
    SelectModule,
    FormsModule,
    ToastModule
  ]
})
export class AdminChebiComponent implements OnInit {
  private controller = inject(ChebiEntryControllerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private jobControllerService = inject(JobsService);
  private loggingService = inject(LoggingService);

  columns: TableColumn[];
  filterFields: string[];
  crudAdapter: CrudAdapter<
    PagedModelEntityModelChebiEntry,
    EntityModelChebiEntry
  >;
  fileOptions: string[];
  file: string;

  ngOnInit() {
    this.jobControllerService.listFilesForJobName("chebiImportJob").subscribe(
      response => {
        this.fileOptions = response?._embedded?.stringList;
      },
      error => {
        this.messageService.add({
          severity: "error",
          summary: "Job config failed",
          detail: "Job config failed to load"
        });
      }
    );
    this.columns = [
      /*{ field: "id", header: "Id", filterType: ColumnFilterType.Text },*/
      {
        field: "id",
        header: "Id",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "transactionUuid",
        header: "Transaction",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "normalizedName",
        header: "Shorthand Name",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "abbreviation",
        header: "Abbreviation",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "nativeId",
        header: "ChEBI Id",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "nativeUrl",
        header: "ChEBI Entry",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "goslinLevel",
        header: "Level",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      {
        field: "synonyms",
        header: "Synonyms",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      },
      // {
      //   field: "name",
      //   header: "Name",
      //   filterType: ColumnFilterType.Text,
      //   content: (value) => value
      // },
      // {
      //   field: "version",
      //   header: "Version",
      //   filterType: ColumnFilterType.Text,
      //   content: (value) => value
      // },
      // {
      //   field: "uri",
      //   header: "Uri",
      //   filterType: ColumnFilterType.Text,
      //   content: (value) => value
      // },
      {
        field: "visibility",
        header: "Visibility",
        filterType: ColumnFilterType.Text,
        content: value => value,
        visible: true
      }
    ];
    this.filterFields = this.columns.map(value => {
      return value.field;
    });
    this.crudAdapter = new ChebiEntryCrudAdapter(
      this.controller,
      this.messageService,
      this.confirmationService,
      this.loggingService
    );
  }

  importChebi() {
    const jobFileInputDto: JobFileInputDto = {
      file: this.file
    };
    this.jobControllerService
      .startJob("chebiImportJob", jobFileInputDto)
      .subscribe(
        response => {
          this.messageService.add({
            severity: "success",
            summary: "Job started",
            detail: "Job started successfully"
          });
        },
        error => {
          this.messageService.add({
            severity: "error",
            summary: "Job failed",
            detail: "Job failed to start"
          });
        }
      );
  }

  reindexChebi() {
    this.confirmationService.confirm({
      header: "Reindex ChEBI links",
      message:
        "Re-scan all lipids and refresh their ChEBI cross-references? This runs a batch job and may take a while.",
      icon: "fa fa-solid fa-triangle-exclamation",
      accept: () => {
        this.jobControllerService.startReindexJob("chebiReindexJob").subscribe({
          next: () => {
            this.messageService.add({
              severity: "success",
              summary: "Reindex started",
              detail: "ChEBI reindex job started successfully"
            });
          },
          error: () => {
            this.messageService.add({
              severity: "error",
              summary: "Reindex failed",
              detail: "ChEBI reindex job failed to start"
            });
          }
        });
      }
    });
  }
}
class ChebiEntryCrudAdapter extends CrudAdapter<
  PagedModelEntityModelChebiEntry,
  EntityModelChebiEntry
> {
  constructor(
    private controller: ChebiEntryControllerService,
    messageService: MessageService,
    confirmationService: ConfirmationService,
    loggingService: LoggingService
  ) {
    super(messageService, confirmationService, loggingService);
  }

  get(
    page?: number,
    size?: number,
    sort?: Array<string>,
    observe?: "body",
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: "application/hal+json";
      context?: HttpContext;
    }
  ): Observable<PagedModelEntityModelChebiEntry> {
    return this.controller.chebiEntryGet(
      page,
      size,
      sort,
      observe,
      reportProgress,
      options
    );
  }

  extractEntries(entries: any): EntityModelChebiEntry[] {
    return <EntityModelChebiEntry[]>entries?._embedded?.chebiEntries;
  }

  saveSingle(
    entity: EntityModelChebiEntry,
    observe?: "body",
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: "application/hal+json";
      context?: HttpContext;
    }
  ): Observable<EntityModelChebiEntry> {
    return this.controller.chebiEntrySaveSingle(
      <ChebiEntry>entity,
      observe,
      reportProgress,
      options
    );
  }
  deleteById(
    id: string,
    observe?: "body",
    reportProgress?: boolean,
    options?: {
      httpHeaderAccept?: "application/hal+json";
      context?: HttpContext;
    }
  ): Observable<object> {
    return this.controller.chebiEntryDeleteById(id, observe, reportProgress, options);
  }
}
